import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { OVERLAY_SLOTS, buildDefaultOverlays, clamp, A5_PORTRAIT_MM } from "./constants";
import { openGiftCardPrint } from "../giftCardPrint";

/**
 * Absolutely-positioned preview that renders every visible overlay
 * with its (x, y, size) from `data.overlays`. Same component is
 * exported for reuse on POS / print, so what the operator sees IS
 * what prints — no separate layout code.
 */
export function GiftCardOverlayPreview({ data, sample = {}, testidPrefix = "gift-preview", showZoneGuides = true }) {
  const bg = data.background_color || "#F9F8F6";
  const bgImg = data.background_image_data_url || "";
  const overlays = data.overlays && Object.keys(data.overlays).length
    ? data.overlays
    : buildDefaultOverlays();
  // Text size sliders map to a percentage of the card's actual pixel
  // width so the value scales predictably as the operator drags. We
  // track the rendered width with a ResizeObserver rather than using
  // a CSS `%` (which resolves against the parent font-size and pins
  // the clamp() minimum, making the slider look dead).
  const cardRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width || 0;
        if (w) setCardWidth(w);
      }
    });
    ro.observe(el);
    setCardWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  const values = useMemo(() => ({
    to_name: sample.to_name || "A Gift For: Guest name",
    from_name: sample.from_name || "From: Buyer name",
    value: sample.value || "IDR 1,000,000",
    expiry: sample.expiry || "Valid until MM / YYYY",
    barcode: sample.barcode || "GC-XXXX-XXXX",
  }), [sample.to_name, sample.from_name, sample.value, sample.expiry, sample.barcode]);

  return (
    <div
      ref={cardRef}
      className="relative w-full rounded-2xl overflow-hidden border border-border shadow-sm"
      style={{
        aspectRatio: `${A5_PORTRAIT_MM.width} / ${A5_PORTRAIT_MM.height}`,
        background: bg,
      }}
      data-testid={testidPrefix}
    >
      {OVERLAY_SLOTS.map((s) => {
        const o = { ...s.defaults, ...(overlays[s.key] || {}) };
        if (!o.visible) return null;
        // z-index: main_image sits at 0 (drawn first, behind
        // everything), the remaining slots stack in the order they
        // appear in OVERLAY_SLOTS so text/barcode/logo always end
        // up on top of the artwork.
        const zIndex = s.key === "main_image" ? 0 : 1;
        const commonStyle = {
          position: "absolute",
          left: `${clamp(o.x, 0, 100)}%`,
          top: `${clamp(o.y, 0, 100)}%`,
          transform: "translate(-50%, -50%)",
          maxWidth: "92%",
          textAlign: "center",
          zIndex,
        };
        // Bounding-box slots — tagline / message / terms. Renders as
        // a fixed rectangle inside which the text wraps naturally.
        // The `size` slider maps to font-size using the SAME formula
        // as the single-line text slots so "Size 5" is identical in
        // pixels no matter which overlay it comes from. The text
        // never auto-scales to fill the box — if it overflows the
        // height it clips (overflow:hidden), it does not shrink.
        if (s.kind === "zone") {
          const w = clamp(o.w ?? s.defaults.w, 10, 100);
          const h = clamp(o.h ?? s.defaults.h, 4, 80);
          const align = o.align || s.defaults.align || "left";
          // Backward-compat: earlier revisions stored the zone font
          // in `font_size`. Fall back to it (scaled 5× so old rows
          // render at the same visual size on the new formula).
          const rawSize = o.size ?? (o.font_size != null ? o.font_size * 5 : s.defaults.size);
          const size = clamp(rawSize, 4, 100);
          const fontPx = cardWidth ? Math.max(8, (size / 100) * cardWidth * 0.4) : 14;
          const text = s.sourceField === "__value__"
            ? String(values.value || "")
            : (String(data[s.sourceField] || "") ||
                (s.sourceField === "tagline" ? "Tagline appears here"
                  : s.sourceField === "terms" ? "Terms & Conditions appear here — long text will wrap inside the box."
                  : "Message appears here — long messages will wrap inside the box you defined."));
          return (
            <div
              key={s.key}
              style={{
                ...commonStyle,
                width: `${w}%`,
                height: `${h}%`,
                maxWidth: "none",
                textAlign: align,
                color: o.color || "#2C2D28",
                fontSize: `${fontPx}px`,
                lineHeight: 1.3,
                overflow: "hidden",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
                textShadow: bgImg ? "0 1px 2px rgba(255,255,255,0.5)" : "none",
                // Dashed guide is editor-only — the print helper drops
                // this style so the paper output stays clean.
                outline: showZoneGuides ? "1.5px dashed rgba(180, 145, 90, 0.9)" : "none",
                outlineOffset: "2px",
                borderRadius: "2px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                padding: "2px 4px",
              }}
              data-testid={`${testidPrefix}-slot-${s.key}`}
              data-font-px={Math.round(fontPx)}
            >
              <span style={{ display: "inline-block", width: "100%", textAlign: align }}>
                {text}
              </span>
            </div>
          );
        }
        if (s.key === "main_image") {
          if (!bgImg) return null;
          // Size slider (20-200) is width % of the card. Height auto
          // via `object-fit: contain` so the image never distorts.
          return (
            <img
              key={s.key}
              src={bgImg}
              alt=""
              draggable={false}
              style={{
                ...commonStyle,
                width: `${clamp(o.size, 20, 200)}%`,
                maxWidth: "none",
                height: "auto",
                objectFit: "contain",
                pointerEvents: "none",
                userSelect: "none",
                transition: "left 120ms ease-out, top 120ms ease-out, width 120ms ease-out",
              }}
              data-testid={`${testidPrefix}-slot-${s.key}`}
            />
          );
        }
        if (s.key === "logo") {
          if (!data.logo_image_data_url) return null;
          return (
            <img
              key={s.key}
              src={data.logo_image_data_url}
              alt="Logo"
              draggable={false}
              style={{ ...commonStyle, width: `${clamp(o.size, 4, 100)}%` }}
              data-testid={`${testidPrefix}-slot-${s.key}`}
            />
          );
        }
        if (s.key === "barcode") {
          const w = clamp(o.size, 4, 100);
          return (
            <div
              key={s.key}
              style={{ ...commonStyle, width: `${w}%` }}
              data-testid={`${testidPrefix}-slot-${s.key}`}
              className="flex flex-col items-center"
            >
              <div
                className="w-full h-6 rounded-sm"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(90deg, #111 0 2px, transparent 2px 4px, #111 4px 5px, transparent 5px 9px, #111 9px 11px, transparent 11px 13px)",
                }}
              />
              <span className="mt-1 text-[10px] font-mono tracking-wider">{values.barcode}</span>
            </div>
          );
        }
        // Text overlays — anchor via `align`:
        //   left   → X = start of text  (translate(0, -50%))
        //   center → X = midpoint       (translate(-50%, -50%))
        //   right  → X = end of text    (translate(-100%, -50%))
        // Same padding + margin on every text slot so numeric X
        // maps to the actual visual anchor pixel.
        const size = clamp(o.size, 4, 100);
        const fontPx = cardWidth ? Math.max(8, (size / 100) * cardWidth * 0.4) : 14;
        const align = (o.align || s.defaults.align || "left").toLowerCase();
        const translateX = align === "center" ? "-50%" : align === "right" ? "-100%" : "0";
        return (
          <div
            key={s.key}
            style={{
              ...commonStyle,
              transform: `translate(${translateX}, -50%)`,
              textAlign: align,
              color: o.color || "#2C2D28",
              fontSize: `${fontPx}px`,
              fontWeight: s.key === "value" ? 700 : 500,
              lineHeight: 1.15,
              textShadow: bgImg ? "0 1px 2px rgba(255,255,255,0.6)" : "none",
              letterSpacing: s.key === "value" ? "-0.01em" : "normal",
              whiteSpace: "nowrap",
              padding: 0,
              margin: 0,
            }}
            data-testid={`${testidPrefix}-slot-${s.key}`}
            data-font-px={Math.round(fontPx)}
            data-align={align}
          >
            {values[s.key] || ""}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Right-hand column of the editor: preview title + Print A5 button
 * on top, live preview below, disclaimer at the bottom.
 */
export default function GiftCardPreview({ form, overlays }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="tracking-overline text-xs text-muted-foreground">LIVE PREVIEW · A5 PORTRAIT (148 × 210 mm)</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => openGiftCardPrint({ ...form, overlays })}
          data-testid="gc-print-preview"
        >
          <Printer className="w-4 h-4 mr-1.5" /> Print A5
        </Button>
      </div>
      <GiftCardOverlayPreview data={{ ...form, overlays }} testidPrefix="gift-preview-editor" />
      <p className="text-[11px] text-muted-foreground">Names, value, expiry and barcode below are placeholders — the POS injects real values when the card is sold. Print output uses the A5 portrait page size.</p>
    </div>
  );
}
